// The Mogul Growth Score™ — Assessment engine data
// 5 pillars × 5 questions per industry. Answer values: 4 = strongest, 0 = critical gap.
// Total score = Math.round( sum(pillarScorePct) / 5 )   where each pillar is 0-100.

export const PILLARS = [
  { key: "strategy", name: "Strategy", icon: "✨", accent: "#C19B54" },
  { key: "operations", name: "Operations", icon: "⚙", accent: "#3B82F6" },
  { key: "brand", name: "Brand Positioning", icon: "◆", accent: "#8B5CF6" },
  { key: "cx", name: "Customer Experience", icon: "★", accent: "#10B981" },
  { key: "leadership", name: "Leadership & Accountability", icon: "▲", accent: "#EF4444" },
];

export const INDUSTRIES = [
  { key: "hospitality", label: "Hospitality (restaurant, bar, lounge, hotel, catering)" },
  { key: "retail", label: "Retail, beauty, or salon" },
  { key: "professional", label: "Professional services or real estate" },
  { key: "healthcare", label: "Healthcare or wellness" },
  { key: "trades", label: "Trades or contractors" },
  { key: "creative", label: "Tech, creative, or agency" },
  { key: "other", label: "Something else" },
];

// ---------- HOSPITALITY (verbatim from Alex's framework) ----------
const HOSPITALITY = {
  strategy: [
    { q: "Does your location have a clear revenue target for this year with a written plan to reach it?", ctx: "Think beyond \"sell more.\" Is there a specific number, an owner, and a weekly check-in?", o: [
      { l: "Yes — specific target, documented plan, tracked weekly.", v: 4 },
      { l: "We have a target but nothing formally written.", v: 2 },
      { l: "Goals are set loosely, usually based on last year.", v: 1 },
      { l: "No formal revenue target exists.", v: 0 },
    ]},
    { q: "How clearly do you understand which guest type — bar, dining, event, regular — generates the most profitable revenue?", ctx: "Not just who spends the most per visit, but who comes back and who drives the highest margin.", o: [
      { l: "Very clearly — we've analyzed by spend, frequency, and margin.", v: 4 },
      { l: "General sense, but no hard data to support it.", v: 2 },
      { l: "We serve all guests the same and haven't broken it down.", v: 1 },
      { l: "Never analyzed this.", v: 0 },
    ]},
    { q: "How deliberate is your pricing strategy across food, drink, and private experiences?", ctx: "Is pricing a strategy or a habit? When did you last review it with margin data?", o: [
      { l: "Very deliberate — we test, adjust, and optimize based on margin.", v: 4 },
      { l: "We revisit pricing when costs rise, not proactively.", v: 2 },
      { l: "Prices were set when we opened and rarely change.", v: 1 },
      { l: "Pricing is mostly based on feel or competitor observation.", v: 0 },
    ]},
    { q: "Do you have a documented growth plan for the next 12 months — new revenue streams, private dining, events?", ctx: "Not intentions — something written, assigned to owners, reviewed regularly.", o: [
      { l: "Yes — documented, assigned, with monthly milestones.", v: 4 },
      { l: "We've talked about it but nothing is written down.", v: 2 },
      { l: "We focus mainly on the current week or month.", v: 1 },
      { l: "No growth plan exists beyond \"do more covers.\"", v: 0 },
    ]},
    { q: "When a major operational or financial decision comes up, how does leadership make the call?", ctx: "Is it data-driven, gut-driven, or whoever speaks loudest?", o: [
      { l: "Based on data — sales reports, covers, and KPI dashboards.", v: 4 },
      { l: "A blend of data and instinct depending on the situation.", v: 2 },
      { l: "Primarily from experience and intuition.", v: 1 },
      { l: "Whoever is most confident in the room.", v: 0 },
    ]},
  ],
  operations: [
    { q: "Does your front-of-house team have a documented, repeatable service sequence for every guest from arrival to departure?", ctx: "From the greeting to the check drop — is there a consistent playbook every team member follows?", o: [
      { l: "Yes — trained, written, and consistently executed.", v: 4 },
      { l: "An informal sequence exists but it's not written down.", v: 2 },
      { l: "Every team member does it their own way.", v: 1 },
      { l: "No defined service sequence exists.", v: 0 },
    ]},
    { q: "How effectively does your team upsell — additional courses, premium drinks, bottle upgrades, or next-visit commitments?", ctx: "Does staff actively guide guests toward more, or wait to be asked?", o: [
      { l: "Very effectively — trained scripts, upsell paths, and measurable results.", v: 4 },
      { l: "Some staff are great at it, others aren't — it's inconsistent.", v: 2 },
      { l: "We rely on the guest to ask rather than proactively offering.", v: 1 },
      { l: "No real upsell or conversion process exists.", v: 0 },
    ]},
    { q: "How well do your bar and kitchen operations work together to increase average guest ticket?", ctx: "Are bartenders recommending food pairings? Are servers directing guests to premium drink options?", o: [
      { l: "Fully integrated — both teams actively cross-sell with knowledge.", v: 4 },
      { l: "They're aware of each other but don't consistently cross-promote.", v: 2 },
      { l: "Bar and kitchen mostly operate as separate experiences.", v: 1 },
      { l: "No intentional coordination between the two.", v: 0 },
    ]},
    { q: "How consistently does your team recover when a guest has a poor or disappointing experience?", ctx: "Think about the last time something went wrong. Was the recovery fast, graceful, and consistent?", o: [
      { l: "We have a defined recovery process every team member follows.", v: 4 },
      { l: "We handle it case by case — some staff are better than others.", v: 2 },
      { l: "Recovery is reactive and inconsistent.", v: 1 },
      { l: "No service recovery process or standard exists.", v: 0 },
    ]},
    { q: "Do you track operational KPIs — covers per shift, average ticket, table turn time — on a regular cadence?", ctx: "Not just \"how much did we make\" but the specific metrics that explain why.", o: [
      { l: "Yes — we have a dashboard reviewed at least weekly.", v: 4 },
      { l: "We track revenue totals but not detailed operational metrics.", v: 2 },
      { l: "We check numbers monthly or when something feels off.", v: 1 },
      { l: "We don't formally track operational KPIs.", v: 0 },
    ]},
  ],
  brand: [
    { q: "Can you describe your restaurant or lounge's identity in one sentence — something only your concept owns?", ctx: "Not \"we serve great food in a nice atmosphere.\" What makes THIS place the one someone drives past another for?", o: [
      { l: "Absolutely — it's documented, trained, and felt in everything we do.", v: 4 },
      { l: "We have a general sense but nothing written or precise.", v: 2 },
      { l: "Our identity is mostly defined by our menu and price point.", v: 1 },
      { l: "We haven't defined a distinct identity beyond our category.", v: 0 },
    ]},
    { q: "How clearly differentiated does your concept feel from other comparable restaurants or lounges in your market?", ctx: "If a customer had to tell a friend why they chose you over the next option, what would they say?", o: [
      { l: "Very clearly — guests choose us for reasons they can articulate.", v: 4 },
      { l: "Somewhat — we're better in ways, but not sharply positioned.", v: 2 },
      { l: "Similar to others but with a different menu or price.", v: 1 },
      { l: "Honestly, we're interchangeable with comparable concepts.", v: 0 },
    ]},
    { q: "How aligned is your current marketing with the specific guest you're trying to attract?", ctx: "Think about your social media, email, and promotions. Does it speak to your ideal regular — or just announce specials?", o: [
      { l: "Fully aligned — content, tone, and targeting reflect our ideal guest.", v: 4 },
      { l: "We post actively but without a tight strategic focus.", v: 2 },
      { l: "We post mostly about daily specials and events without strategy.", v: 1 },
      { l: "Marketing is minimal and mostly reactive.", v: 0 },
    ]},
    { q: "How intentionally do you leverage your location, neighborhood, or environment as a brand advantage?", ctx: "Is your address part of your story — or just where you happen to be?", o: [
      { l: "Very intentionally — location is woven into our positioning and experience.", v: 4 },
      { l: "We benefit from our location but don't actively market the connection.", v: 2 },
      { l: "We haven't thought about location as a brand asset.", v: 1 },
      { l: "Our location works against our brand more than for it.", v: 0 },
    ]},
    { q: "How consistent is your brand experience from how you look online to how guests feel when they walk in?", ctx: "Does what they see on social media match what they experience at the table or the bar?", o: [
      { l: "Highly consistent — online and in-person feel like the same brand.", v: 4 },
      { l: "Mostly consistent — minor gaps between promise and delivery.", v: 2 },
      { l: "Noticeable gap between our online image and actual experience.", v: 1 },
      { l: "We haven't thought about this alignment.", v: 0 },
    ]},
  ],
  cx: [
    { q: "What percentage of first-time guests do you estimate return to your venue within 30 days?", ctx: "Not who comes back eventually — who comes back within a month. This is your early retention signal.", o: [
      { l: "Above 40% — strong early return behavior.", v: 4 },
      { l: "Around 20–40% — decent but room to improve.", v: 2 },
      { l: "Below 20% — most first-timers don't return quickly.", v: 1 },
      { l: "We don't track return visit rates.", v: 0 },
    ]},
    { q: "Does your team actively capture guest contact information — email, phone, or SMS — at or near the point of service?", ctx: "Not a sign-in sheet that lives in a drawer. An actual system with a capture habit, data storage, and a follow-up process.", o: [
      { l: "Yes — consistent system, capturing 60%+ of first-time guests.", v: 4 },
      { l: "We try to collect it, but it's inconsistent across shifts and staff.", v: 2 },
      { l: "We have a method, but the data rarely gets used.", v: 1 },
      { l: "We don't actively capture guest contact information.", v: 0 },
    ]},
    { q: "After a guest visits or attends an event, what happens in the next 48 hours?", ctx: "Think about your last busy Friday night. What did those guests receive from you on Sunday?", o: [
      { l: "They receive a direct follow-up with a reason to return.", v: 4 },
      { l: "We might post on social but no direct guest follow-up.", v: 2 },
      { l: "Nothing formal — we move on to the next service.", v: 1 },
      { l: "No post-visit communication process exists.", v: 0 },
    ]},
    { q: "How personalized does the experience feel for your regulars — do they feel genuinely known?", ctx: "Can your staff name their preferred drink, their usual table, what they do for a living?", o: [
      { l: "Very — regulars are known by name, preference, and occasion.", v: 4 },
      { l: "For some regulars yes — depends on the staff member.", v: 2 },
      { l: "We recognize faces, but don't have deep guest knowledge.", v: 1 },
      { l: "Most guests feel like a new interaction every visit.", v: 0 },
    ]},
    { q: "Do you have a structured VIP, loyalty, or membership program for high-value guests?", ctx: "Not \"we treat regulars well.\" A system with criteria, benefits, and communication that makes top guests feel they have a home.", o: [
      { l: "Yes — structured, communicated clearly, and actively enrolled.", v: 4 },
      { l: "Informal recognition only — nothing formalized.", v: 2 },
      { l: "We treat all guests the same regardless of frequency.", v: 1 },
      { l: "No loyalty or VIP program exists.", v: 0 },
    ]},
  ],
  leadership: [
    { q: "How clearly are performance expectations communicated to your floor and bar team?", ctx: "Not just \"be friendly and upsell.\" Are there specific standards every team member knows and is reviewed on?", o: [
      { l: "Very clearly — documented, trained, and reviewed in regular check-ins.", v: 4 },
      { l: "Communicated verbally but inconsistently across managers and shifts.", v: 2 },
      { l: "Team members generally know what's expected but nothing is formalized.", v: 1 },
      { l: "Performance expectations are rarely explicitly communicated.", v: 0 },
    ]},
    { q: "How does management respond when a revenue or covers target is missed for the week?", ctx: "Is there a root-cause conversation — or does the team just move forward?", o: [
      { l: "We diagnose the root cause, identify what changed, and adjust tactics.", v: 4 },
      { l: "We discuss it in team meetings but don't always change approach.", v: 2 },
      { l: "We acknowledge it internally and move on without a structured review.", v: 1 },
      { l: "We don't have a consistent process for reviewing performance misses.", v: 0 },
    ]},
    { q: "How consistent is your leadership presence across every shift and every day of the week?", ctx: "Is the standard the same on a Tuesday lunch as it is on a Saturday night?", o: [
      { l: "Highly consistent — standards hold regardless of shift or manager on duty.", v: 4 },
      { l: "Mostly consistent — a few shifts drift from the standard.", v: 2 },
      { l: "Noticeably different depending on who's leading.", v: 1 },
      { l: "The standard depends entirely on who's in the building.", v: 0 },
    ]},
    { q: "How is your team held accountable for hitting goals, standards, and service outcomes?", ctx: "Accountability isn't punishment — it's a rhythm of clear expectations, honest review, and follow-through.", o: [
      { l: "Weekly one-on-ones, clear scorecards, and follow-through on both sides.", v: 4 },
      { l: "Regular check-ins but accountability is inconsistent.", v: 2 },
      { l: "Feedback usually only happens when there's a problem.", v: 1 },
      { l: "No consistent accountability rhythm exists.", v: 0 },
    ]},
    { q: "How much of the business is dependent on the owner or a single leader being present?", ctx: "If you stepped away for two weeks, would revenue, service, and standards hold?", o: [
      { l: "The business runs strongly whether or not any single person is on-site.", v: 4 },
      { l: "It runs, but not at full quality without leadership presence.", v: 2 },
      { l: "Service and standards drop noticeably without the owner or key leader.", v: 1 },
      { l: "The business barely functions without one specific person.", v: 0 },
    ]},
  ],
};

// ---------- GENERIC (industry-agnostic — used for retail/professional/healthcare/trades/creative/other) ----------
const GENERIC = {
  strategy: [
    { q: "Does your business have a clear revenue target for this year with a written plan to reach it?", ctx: "Think beyond \"grow the business.\" Is there a specific number, an owner, and a weekly check-in?", o: [
      { l: "Yes — specific target, documented plan, tracked weekly.", v: 4 },
      { l: "We have a target but nothing formally written.", v: 2 },
      { l: "Goals are set loosely, usually based on last year.", v: 1 },
      { l: "No formal revenue target exists.", v: 0 },
    ]},
    { q: "How clearly do you understand which customer type or service line generates the most profitable revenue?", ctx: "Not just who spends the most, but who returns and who drives the highest margin.", o: [
      { l: "Very clearly — we've analyzed by spend, frequency, and margin.", v: 4 },
      { l: "General sense, but no hard data to support it.", v: 2 },
      { l: "We serve everyone the same and haven't broken it down.", v: 1 },
      { l: "Never analyzed this.", v: 0 },
    ]},
    { q: "How deliberate is your pricing strategy across your services or products?", ctx: "Is pricing a strategy or a habit? When did you last review it with margin data?", o: [
      { l: "Very deliberate — we test, adjust, and optimize based on margin.", v: 4 },
      { l: "We revisit pricing when costs rise, not proactively.", v: 2 },
      { l: "Prices were set early on and rarely change.", v: 1 },
      { l: "Pricing is mostly based on feel or competitor observation.", v: 0 },
    ]},
    { q: "Do you have a documented growth plan for the next 12 months?", ctx: "Not intentions — something written, assigned to owners, reviewed regularly.", o: [
      { l: "Yes — documented, assigned, with monthly milestones.", v: 4 },
      { l: "We've talked about it but nothing is written down.", v: 2 },
      { l: "We focus mainly on the current week or month.", v: 1 },
      { l: "No growth plan exists beyond \"do more.\"", v: 0 },
    ]},
    { q: "When a major operational or financial decision comes up, how does leadership make the call?", ctx: "Is it data-driven, gut-driven, or whoever speaks loudest?", o: [
      { l: "Based on data — reports and KPI dashboards.", v: 4 },
      { l: "A blend of data and instinct depending on the situation.", v: 2 },
      { l: "Primarily from experience and intuition.", v: 1 },
      { l: "Whoever is most confident in the room.", v: 0 },
    ]},
  ],
  operations: [
    { q: "Does your team have a documented, repeatable process for delivering your core service or product?", ctx: "Is there a consistent playbook every team member follows — from first touch to completion?", o: [
      { l: "Yes — trained, written, and consistently executed.", v: 4 },
      { l: "An informal process exists but it's not written down.", v: 2 },
      { l: "Every team member does it their own way.", v: 1 },
      { l: "No defined process exists.", v: 0 },
    ]},
    { q: "How effectively does your team upsell, cross-sell, or expand engagements with existing customers?", ctx: "Does your team actively grow existing relationships, or wait for customers to ask?", o: [
      { l: "Very effectively — trained approach with measurable results.", v: 4 },
      { l: "Some team members are great at it, others aren't — inconsistent.", v: 2 },
      { l: "We rely on customers to ask rather than proactively offering.", v: 1 },
      { l: "No real upsell or expansion process exists.", v: 0 },
    ]},
    { q: "How well do the different parts of your business (delivery, sales, marketing) work together to grow customer value?", ctx: "Are teams sharing information and coordinating around the customer, or operating in silos?", o: [
      { l: "Fully integrated — teams actively coordinate around the customer.", v: 4 },
      { l: "They're aware of each other but don't consistently coordinate.", v: 2 },
      { l: "Teams mostly operate as separate functions.", v: 1 },
      { l: "No intentional coordination between functions.", v: 0 },
    ]},
    { q: "How consistently does your team recover when a customer has a poor or disappointing experience?", ctx: "Think about the last time something went wrong. Was the recovery fast, graceful, and consistent?", o: [
      { l: "We have a defined recovery process every team member follows.", v: 4 },
      { l: "We handle it case by case — some team members better than others.", v: 2 },
      { l: "Recovery is reactive and inconsistent.", v: 1 },
      { l: "No service recovery process or standard exists.", v: 0 },
    ]},
    { q: "Do you track operational KPIs on a regular cadence?", ctx: "Not just revenue — the specific metrics that explain why the business is (or isn't) growing.", o: [
      { l: "Yes — we have a dashboard reviewed at least weekly.", v: 4 },
      { l: "We track revenue totals but not detailed operational metrics.", v: 2 },
      { l: "We check numbers monthly or when something feels off.", v: 1 },
      { l: "We don't formally track operational KPIs.", v: 0 },
    ]},
  ],
  brand: [
    { q: "Can you describe your business's identity in one sentence — something only you own?", ctx: "Not a generic promise. What makes yours the one someone chooses over another?", o: [
      { l: "Absolutely — it's documented, trained, and felt in everything we do.", v: 4 },
      { l: "We have a general sense but nothing written or precise.", v: 2 },
      { l: "Our identity is mostly defined by our category and price point.", v: 1 },
      { l: "We haven't defined a distinct identity beyond our category.", v: 0 },
    ]},
    { q: "How clearly differentiated does your business feel from comparable options in your market?", ctx: "If a customer had to tell a friend why they chose you over the next option, what would they say?", o: [
      { l: "Very clearly — customers choose us for reasons they can articulate.", v: 4 },
      { l: "Somewhat — we're better in ways, but not sharply positioned.", v: 2 },
      { l: "Similar to others but with a different offer or price.", v: 1 },
      { l: "Honestly, we're interchangeable with comparable businesses.", v: 0 },
    ]},
    { q: "How aligned is your current marketing with the specific customer you're trying to attract?", ctx: "Does your content speak to your ideal customer — or just announce that you exist?", o: [
      { l: "Fully aligned — content, tone, and targeting reflect our ideal customer.", v: 4 },
      { l: "We market actively but without a tight strategic focus.", v: 2 },
      { l: "We market mostly reactively — announcements, promotions.", v: 1 },
      { l: "Marketing is minimal and mostly reactive.", v: 0 },
    ]},
    { q: "How intentionally do you leverage your community or environment as a brand advantage?", ctx: "Is your location, network, or story part of your brand — or just where you happen to be?", o: [
      { l: "Very intentionally — it's woven into our positioning.", v: 4 },
      { l: "We benefit from it but don't actively market the connection.", v: 2 },
      { l: "We haven't thought about it as a brand asset.", v: 1 },
      { l: "It works against our brand more than for it.", v: 0 },
    ]},
    { q: "How consistent is your brand from how you look online to how customers feel when they interact with you?", ctx: "Does what they see online match what they experience in real life?", o: [
      { l: "Highly consistent — online and in-person feel like the same brand.", v: 4 },
      { l: "Mostly consistent — minor gaps between promise and delivery.", v: 2 },
      { l: "Noticeable gap between our online image and actual experience.", v: 1 },
      { l: "We haven't thought about this alignment.", v: 0 },
    ]},
  ],
  cx: [
    { q: "What percentage of first-time customers do you estimate return or re-engage within 30 days?", ctx: "This is your early retention signal — arguably the most important number in the business.", o: [
      { l: "Above 40% — strong early return behavior.", v: 4 },
      { l: "Around 20–40% — decent but room to improve.", v: 2 },
      { l: "Below 20% — most first-timers don't return quickly.", v: 1 },
      { l: "We don't track return rates.", v: 0 },
    ]},
    { q: "Does your team actively capture customer contact information at or near the first interaction?", ctx: "An actual system with a capture habit, data storage, and a follow-up process — not a form that lives in a drawer.", o: [
      { l: "Yes — consistent system, capturing 60%+ of first-time customers.", v: 4 },
      { l: "We try to collect it, but it's inconsistent.", v: 2 },
      { l: "We have a method, but the data rarely gets used.", v: 1 },
      { l: "We don't actively capture customer contact information.", v: 0 },
    ]},
    { q: "After a customer interaction, what happens in the next 48 hours?", ctx: "Think about your last busy day. What did those customers receive from you afterward?", o: [
      { l: "They receive a direct follow-up with a reason to return.", v: 4 },
      { l: "We might post on social but no direct customer follow-up.", v: 2 },
      { l: "Nothing formal — we move on to the next opportunity.", v: 1 },
      { l: "No post-interaction communication process exists.", v: 0 },
    ]},
    { q: "How personalized does the experience feel for your repeat customers — do they feel genuinely known?", ctx: "Can your team name their preferences, history, or context without checking notes?", o: [
      { l: "Very — repeat customers are known by name, preference, and context.", v: 4 },
      { l: "For some yes — depends on the team member.", v: 2 },
      { l: "We recognize faces, but don't have deep customer knowledge.", v: 1 },
      { l: "Most customers feel like a new interaction every time.", v: 0 },
    ]},
    { q: "Do you have a structured VIP, loyalty, or referral program for high-value customers?", ctx: "A system with criteria, benefits, and communication that makes your top customers feel they have a home.", o: [
      { l: "Yes — structured, communicated clearly, and actively enrolled.", v: 4 },
      { l: "Informal recognition only — nothing formalized.", v: 2 },
      { l: "We treat all customers the same regardless of frequency.", v: 1 },
      { l: "No loyalty or VIP program exists.", v: 0 },
    ]},
  ],
  leadership: [
    { q: "How clearly are performance expectations communicated to your team?", ctx: "Are there specific standards every team member knows and is reviewed on?", o: [
      { l: "Very clearly — documented, trained, and reviewed in regular check-ins.", v: 4 },
      { l: "Communicated verbally but inconsistently.", v: 2 },
      { l: "Team members generally know but nothing is formalized.", v: 1 },
      { l: "Performance expectations are rarely explicitly communicated.", v: 0 },
    ]},
    { q: "How does management respond when a revenue or performance target is missed?", ctx: "Is there a root-cause conversation — or does the team just move forward?", o: [
      { l: "We diagnose the root cause, identify what changed, and adjust tactics.", v: 4 },
      { l: "We discuss it but don't always change approach.", v: 2 },
      { l: "We acknowledge it and move on without a structured review.", v: 1 },
      { l: "We don't have a consistent process for reviewing performance misses.", v: 0 },
    ]},
    { q: "How consistent is your leadership presence across every part of the business?", ctx: "Is the standard the same in every function, on every day?", o: [
      { l: "Highly consistent — standards hold across the entire business.", v: 4 },
      { l: "Mostly consistent — a few areas drift from the standard.", v: 2 },
      { l: "Noticeably different depending on who's leading.", v: 1 },
      { l: "The standard depends entirely on who's involved.", v: 0 },
    ]},
    { q: "How is your team held accountable for hitting goals, standards, and outcomes?", ctx: "A rhythm of clear expectations, honest review, and follow-through.", o: [
      { l: "Weekly one-on-ones, clear scorecards, and follow-through.", v: 4 },
      { l: "Regular check-ins but accountability is inconsistent.", v: 2 },
      { l: "Feedback usually only happens when there's a problem.", v: 1 },
      { l: "No consistent accountability rhythm exists.", v: 0 },
    ]},
    { q: "How much of the business is dependent on the owner or a single leader being present?", ctx: "If you stepped away for two weeks, would revenue, quality, and standards hold?", o: [
      { l: "The business runs strongly regardless of any single person.", v: 4 },
      { l: "It runs, but not at full quality without leadership present.", v: 2 },
      { l: "Standards drop noticeably without the owner or key leader.", v: 1 },
      { l: "The business barely functions without one specific person.", v: 0 },
    ]},
  ],
};

export const QUESTION_BANKS = {
  hospitality: HOSPITALITY,
  retail: GENERIC,
  professional: GENERIC,
  healthcare: GENERIC,
  trades: GENERIC,
  creative: GENERIC,
  other: GENERIC,
};

// ---------- SCORING ----------
export function computePillarScore(pillarKey, answers, industry) {
  const bank = QUESTION_BANKS[industry] || GENERIC;
  const questions = bank[pillarKey] || [];
  if (!questions.length) return 0;
  const max = questions.length * 4;
  const total = questions.reduce((sum, _, i) => {
    const idx = `${pillarKey}-${i}`;
    return sum + (answers[idx] ?? 0);
  }, 0);
  return Math.round((total / max) * 100);
}

export function computeTotalScore(answers, industry) {
  const scores = PILLARS.map((p) => computePillarScore(p.key, answers, industry));
  const avg = scores.reduce((a, b) => a + b, 0) / PILLARS.length;
  return { total: Math.round(avg), pillars: scores };
}

// ---------- TIERS ----------
export function tierForScore(score) {
  if (score >= 80) return { key: "elite", name: "Elite Operator", accent: "#10B981", body: "Your systems and strategy are strong across most pillars. The focus now is refinement, scale, and eliminating the last remaining gaps." };
  if (score >= 65) return { key: "growth", name: "Growth Ready", accent: "#C19B54", body: "A solid foundation exists across your business. Specific, targeted fixes in your weakest areas will unlock your next revenue tier." };
  if (score >= 45) return { key: "building", name: "Building Stage", accent: "#3B82F6", body: "Real strengths are present — but inconsistency across key systems is creating revenue leakage. Structured support accelerates progress significantly." };
  return { key: "optimize", name: "Optimization Needed", accent: "#EF4444", body: "Foundational systems need to be built or rebuilt. The good news: businesses at this stage see the fastest gains once the right structure is in place." };
}

export function pillarLabel(score) {
  if (score >= 80) return { label: "Strong", accent: "#10B981" };
  if (score >= 60) return { label: "Developing", accent: "#C19B54" };
  if (score >= 40) return { label: "Needs Work", accent: "#3B82F6" };
  return { label: "Critical Gap", accent: "#EF4444" };
}

// ---------- ALEX'S PERSONALIZED QUOTE ----------
export function mogulQuote(score, firstName, company) {
  const name = firstName || "there";
  const co = company || "your business";
  if (score >= 80) return `${name}, you're already operating with more clarity than most business owners in your industry. The Mogul Group Advisors isn't here to rebuild what's working — we're here to make sure your ceiling becomes your new floor. Let's scale this.`;
  if (score >= 65) return `You have a real business with real potential, ${name}. The gaps we identified aren't problems — they're opportunities. Most businesses at your stage are one or two focused improvements away from a completely different revenue reality. That's exactly the work we do.`;
  if (score >= 45) return `${name}, the foundation of ${co} is more solid than your score might suggest. What's missing are the systems that make your strengths repeatable and your weak spots predictable. That's not a hustle problem — it's a structure problem. We solve those.`;
  return `Every elite business you admire started where ${co} is right now — with more potential than infrastructure. The Mogul Growth Framework™ exists precisely for this stage. When we build the foundation right, the growth that follows is faster, cleaner, and more sustainable. Let's build it.`;
}

// ---------- PILLAR-LEVEL ACTION COPY ----------
// Keyed by pillar, then by band (high/mid/low)
export const PILLAR_ACTIONS = {
  strategy: {
    high: { title: "Your strategy is a real asset.", body: "Formalize your 12-month plan review cadence and share it across leadership so the discipline compounds." },
    mid: { title: "Strategy is present but under-documented.", body: "Establish one clear revenue target with a written plan, an owner, and a weekly review — that alone lifts execution meaningfully." },
    low: { title: "Strategy is your highest-leverage fix.", body: "Define your most important revenue target for the next quarter, document a plan to hit it, and share it with your team this week." },
  },
  operations: {
    high: { title: "Operations are running with discipline.", body: "Push for a KPI dashboard reviewed weekly and codify your best playbooks as trainable SOPs." },
    mid: { title: "Operations work — but not repeatably.", body: "Document your core service sequence and pick 2–3 KPIs to review weekly. Consistency is the multiplier." },
    low: { title: "Operations are the fastest revenue unlock.", body: "Write down your top delivery process step-by-step, appoint one owner, and start tracking one meaningful metric weekly." },
  },
  brand: {
    high: { title: "Your brand punches above its weight.", body: "Codify your brand voice and story so every team member can carry it into every interaction." },
    mid: { title: "Brand is present but not sharply positioned.", body: "Sharpen the one sentence that only your business can claim, and align your online presence with the real in-person experience." },
    low: { title: "Brand differentiation is your growth ceiling.", body: "Define who you are, who you're for, and what only you do — then rebuild your marketing around that clarity." },
  },
  cx: {
    high: { title: "Customer experience is a competitive moat.", body: "Formalize a VIP program and post-visit follow-up rhythm to lock in the loyalty you've already built." },
    mid: { title: "The experience is good — the systems are missing.", body: "Build a consistent contact-capture habit and a 48-hour follow-up so first-time customers become repeat ones." },
    low: { title: "CX systems are where revenue leaks.", body: "Start capturing every customer's contact info and follow up within 48 hours. Retention beats acquisition every time." },
  },
  leadership: {
    high: { title: "Leadership is a strength you can lean on.", body: "Push for owner-independence: standards should hold whether or not you're in the building." },
    mid: { title: "Leadership presence is real but uneven.", body: "Establish weekly one-on-ones with clear scorecards. Accountability is a rhythm, not a punishment." },
    low: { title: "Leadership clarity will change everything downstream.", body: "Set clear expectations, document standards, and start a weekly accountability rhythm. Everything else compounds from here." },
  },
};

export function pillarAction(pillarKey, score) {
  const band = score >= 65 ? "high" : score >= 45 ? "mid" : "low";
  return PILLAR_ACTIONS[pillarKey]?.[band] || { title: "", body: "" };
}
